import { useTranslation } from 'react-i18next';
import { canonicalizeColor } from '@complan/domain';
import { Field } from '@/features/initiative/intake/fields';

/**
 * The Platform bar-colour picker (task 23.6, R16.12).
 *
 * The colour rules - a well-formed hex value and uniqueness among active
 * Platforms - are computed and enforced in the domain (`validatePlatformColor`)
 * and returned by the API as findings on save. The former contrast minimum and
 * active-Platform cap were removed by product decision. This picker does **not**
 * re-evaluate the rules: it lets an administrator choose a colour and shows a
 * live swatch, and a clash with another active Platform is surfaced by the editor
 * dialog's findings region.
 *
 * ## Notation, and why the field rewrites what you type
 *
 * The stored spelling is lowercase `#rrggbb`, because R16.12's uniqueness is
 * plain string equality (see `HexColor` in `@complan/domain`). A colour is
 * routinely handed to a person in some other notation, though - a design tool
 * gives `#FB7702`, CSS allows `#fb7` - and rejecting those on save reads as "this
 * colour is invalid" when it is only spelled differently. So the text field
 * accepts any notation `canonicalizeColor` understands and settles to the stored
 * spelling as soon as the entry resolves to a colour, which means what is on
 * screen is what will be saved. Anything that resolves to no colour is left
 * exactly as typed, so a genuine mistake still reaches the findings region rather
 * than being quietly replaced.
 */
export function PlatformColourPicker({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (colour: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  // The native colour input demands a `#rrggbb`. Canonicalising here means the
  // swatch tracks any accepted notation, and a half-typed entry falls back rather
  // than making the control error.
  const swatchValue = canonicalizeColor(value) ?? '#000000';

  return (
    <Field
      label={t('admin.masterData.editor.colour')}
      htmlFor="md-colour"
      hint={t('admin.masterData.editor.colourHint')}
    >
      <div className="flex items-center gap-2">
        <input
          aria-label={t('admin.masterData.editor.colour')}
          type="color"
          value={swatchValue}
          onChange={(event) => onChange(event.target.value)}
          className="border-line h-8 w-10 rounded-[var(--radius-control)] border bg-panel"
        />
        <input
          id="md-colour"
          type="text"
          value={value}
          // Long enough for `rgba(255, 255, 255, 0.5)`; the six-digit stored form
          // is far shorter, but the field accepts what it accepts.
          maxLength={30}
          spellCheck={false}
          autoComplete="off"
          onChange={(event) => onChange(event.target.value)}
          // Settle to the stored spelling once focus leaves, so the administrator
          // sees the value that will be persisted and compared.
          onBlur={(event) => {
            const canonical = canonicalizeColor(event.target.value);
            if (canonical !== undefined && canonical !== event.target.value) {
              onChange(canonical);
            }
          }}
          className="border-line h-8 w-40 rounded-[var(--radius-control)] border bg-panel px-2 font-mono text-[12.5px] text-txt outline-none focus-visible:border-[var(--accent)]"
        />
      </div>
    </Field>
  );
}
